import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import authService from '../api/authService';
import { usePostHog } from './PostHogContext';

// Create the auth context
const AuthContext = createContext();

// Token refresh interval in milliseconds (30 minutes)
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000;

// Hot reload test comment - trigger reload
// Initialize Pendo with user data
const initializePendo = (user) => {
  if (typeof window !== 'undefined' && window.pendo) {
    try {
      // Extract user information for Pendo
      const visitor = {
        id: user.id || user.email, // Use user ID or email as fallback
        email: user.email,
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
      };

      // Extract account information (assuming group-based accounts)
      const account = {
        id: user.groupId || user.id, // Use group ID or user ID as fallback
        accountName: user.groupName || user.email, // Use group name or email as fallback
        payingStatus: user.membershipStatus || 'free', // Use membership status
        location: user.location || user.city || '',
        accountCreationDate: user.createdAt || user.created_at || new Date().toISOString(),
      };

      console.log('🔍 Pendo: Initializing with user data:', { visitor, account });
      
      window.pendo.initialize({
        visitor,
        account
      });
    } catch (error) {
      console.error('❌ Pendo initialization error:', error);
    }
  }
};

// Initialize PostHog with user data
const initializePostHog = (user, postHogContext) => {
  if (postHogContext && postHogContext.isInitialized) {
    try {
      // Identify the user in PostHog
      const userId = user.id || user.email;
      const userProperties = {
        email: user.email,
        firstName: user.firstName || user.first_name || '',
        lastName: user.lastName || user.last_name || '',
        role: user.role || 'member',
        membershipStatus: user.membershipStatus || 'free',
        groupId: user.groupId || null,
        groupName: user.groupName || '',
        location: user.location || user.city || '',
        createdAt: user.createdAt || user.created_at,
        hasCompletedOnboarding: user.hasCompletedOnboarding || false,
      };

      console.log('🔍 PostHog: Identifying user:', { userId, userProperties });
      
      postHogContext.identifyUser(userId, userProperties);
      
      // Track login event
      postHogContext.trackEvent('user_login', {
        login_method: 'email', // You can make this dynamic based on login type
        user_role: user.role || 'member',
        membership_status: user.membershipStatus || 'free',
      });
    } catch (error) {
      console.error('❌ PostHog initialization error:', error);
    }
  }
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Add logout state
  const refreshTimerRef = useRef(null);
  
  // Get PostHog context
  const postHogContext = usePostHog();

  // Get cached user role for redirect decisions
  const getCachedUserRole = () => {
    const cachedUser = authService.getCurrentUser();
    return cachedUser?.role || 'member';
  };

  // Refresh user profile from server
  const refreshUserProfile = async () => {
    try {
      const updatedUser = await authService.refreshUserProfile();
      setCurrentUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error('Error refreshing user profile:', err);
    }
  };

  // Handle session refresh when token is expired
  const refreshSession = async () => {
    try {
      setError('');
      setTokenExpired(false);
      
      // Try to refresh the token
      const data = await authService.refreshToken();
      
      if (data && data.user) {
        setCurrentUser(data.user);
        return data;
      } else {
        throw new Error('No user data received');
      }
    } catch (err) {
      // If refresh fails, clear everything and let the UI handle redirect
      authService.logout();
      setCurrentUser(null);
      setTokenExpired(false);
      throw err;
    }
  };

  // Handle token refresh
  const handleTokenRefresh = async () => {
    try {
      if (authService.isAuthenticated()) {
        await refreshAuthToken();
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      // If token refresh fails, we do not log out the user immediately
      // We'll let the token expire naturally and try again later
    }
  };

  // Setup periodic token refresh
  const setupTokenRefresh = () => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    
    // Set up new timer for token refresh
    refreshTimerRef.current = setInterval(handleTokenRefresh, TOKEN_REFRESH_INTERVAL);
    
    // Also refresh immediately if token exists
    if (authService.isAuthenticated()) {
      handleTokenRefresh();
    }
  };

  // Initialize auth state on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 AuthContext - Starting initialization...');
      try {
        const token = localStorage.getItem('token');
        
        console.log('🚀 AuthContext - token exists:', !!token);
        
        if (token) {
          // 🚨 FIX: Check if token is expired BEFORE trying to validate
          if (!authService.isAuthenticated()) {
            console.log('🔒 SECURITY: Token is expired based on expiry time - showing refresh interface');
            setTokenExpired(true);
            setCurrentUser(null);
            setLoading(false);
            return;
          }
          
          console.log('🚀 AuthContext - Found valid token, validating with server...');
          
          try {
            // Always validate token with server first
            const validationResult = await authService.validateToken();
            
            if (validationResult.valid) {
              // Token is valid, use cached user data or fetch fresh
              const cachedUser = authService.getCurrentUser();
              if (cachedUser) {
                console.log('🔒 SECURITY: Token valid, using cached user data for:', cachedUser.email);
                setCurrentUser(cachedUser);
                // Initialize Pendo for existing authenticated user
                initializePendo(cachedUser);
                // Initialize PostHog for existing authenticated user
                initializePostHog(cachedUser, postHogContext);
              } else {
                console.log('🔒 SECURITY: Token valid but no cached user, fetching from server...');
                const freshUser = await authService.refreshUserProfile();
                if (freshUser) {
                  setCurrentUser(freshUser);
                  // Initialize Pendo for freshly fetched user
                  initializePendo(freshUser);
                  // Initialize PostHog for freshly fetched user
                  initializePostHog(freshUser, postHogContext);
                  console.log('🔒 SECURITY: Server validation successful for user:', freshUser.email);
                } else {
                  throw new Error('Invalid user data received from server');
                }
              }
            } else {
              // Token is invalid, show expired interface
              console.log('🔒 SECURITY: Token validation failed, showing refresh interface');
              setTokenExpired(true);
              setCurrentUser(null);
            }
          } catch (err) {
            console.error('🔒 SECURITY: Token validation failed:', err);
            
            // 🚨 FIX: Only show session expired message for actual token expiration
            // Check if this is a 401/403 error (actual auth failure) vs network/server error
            if (err.response?.status === 401 || err.response?.status === 403) {
              console.log('🔒 SECURITY: Token is actually expired or invalid - showing refresh interface');
              setTokenExpired(true);
            } else if (err.response?.status === 404) {
              console.log('🔒 SECURITY: Token validation endpoint not found (404) - using cached user data');
              // 404 means the validation endpoint doesn't exist - this is a server config issue, not token expiry
              const cachedUser = authService.getCurrentUser();
              if (cachedUser) {
                console.log('🔒 SECURITY: Using cached user data due to missing validation endpoint for:', cachedUser.email);
                setCurrentUser(cachedUser);
                return; // Don't set currentUser to null below
              } else {
                console.log('🔒 SECURITY: No cached user available, clearing auth state');
                authService.logout();
              }
            } else {
              console.log('🔒 SECURITY: Network/server error during validation - using cached user data if available');
              // For other network errors, try to use cached user data
              const cachedUser = authService.getCurrentUser();
              if (cachedUser) {
                console.log('🔒 SECURITY: Using cached user data during network issues for:', cachedUser.email);
                setCurrentUser(cachedUser);
                return; // Don't set currentUser to null below
              } else {
                // No cached user and can't validate - this might be a real auth issue
                console.log('🔒 SECURITY: No cached user and validation failed - clearing auth state');
                authService.logout();
              }
            }
            setCurrentUser(null);
          }
        } else {
          console.log('🚀 AuthContext - No token - user is not logged in');
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('🚀 AuthContext - Auth initialization error:', err);
        // On any unexpected error, only show refresh interface if we had a token AND it's an auth error
        const token = localStorage.getItem('token');
        if (token && (err.response?.status === 401 || err.response?.status === 403)) {
          setTokenExpired(true);
        }
        setCurrentUser(null);
      } finally {
        console.log('🚀 AuthContext - Initialization complete, setting loading to false');
        setLoading(false);
      }
    };

    initializeAuth();
    setupTokenRefresh();

    // Clean up timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []); // 🔒 CRITICAL: Only run once on mount, not on every user change

  // Register a new user
  const register = async (userData) => {
    try {
      setError('');
      const data = await authService.register(userData);
      
      // 🚨 CRITICAL FIX: Use the user data from registration response directly
      // Don't fetch from server again to avoid user data changes
      if (data.user) {
        console.log('🔒 SECURITY: Registration successful for:', data.user.email, 'ID:', data.user.id);
        
        // 🚨 EMERGENCY FIX: Set user immediately without server validation to prevent data changes
        setCurrentUser(data.user);
        // Initialize Pendo for newly registered user
        initializePendo(data.user);
        // Initialize PostHog for newly registered user
        initializePostHog(data.user, postHogContext);
        // Track registration event
        postHogContext?.trackEvent('user_registered', {
          registration_method: 'email',
          user_role: data.user.role || 'member',
        });
      } else {
        setCurrentUser(data.user);
      }
      
      return data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
      throw err;
    }
  };

  // Register as admin
  const registerAdmin = async (userData) => {
    try {
      setError('');
      const data = await authService.registerAdmin(userData);
      
      // 🚨 CRITICAL FIX: Use the user data from registration response directly
      // Don't fetch from server again to avoid user ID changes
      if (data.user) {
        console.log('🔒 SECURITY: Admin registration successful for:', data.user.email, 'ID:', data.user.id);
        
        // 🚨 EMERGENCY FIX: Set user immediately without server validation to prevent ID changes
        setCurrentUser(data.user);
        // Initialize Pendo for newly registered admin
        initializePendo(data.user);
        // Initialize PostHog for newly registered admin
        initializePostHog(data.user, postHogContext);
        // Track admin registration event
        postHogContext?.trackEvent('admin_registered', {
          registration_method: 'email',
          user_role: 'admin',
        });
        
        // 🔧 OPTIONAL: Validate in background but don't change the user if validation succeeds
        try {
          console.log('🔒 SECURITY: Background validation of admin registration data');
          const freshUser = await authService.refreshUserProfile();
          
          // Only log mismatch but don't change the user to prevent ID conflicts
          if (freshUser && freshUser.id !== data.user.id) {
            console.warn('🔒 SECURITY: WARNING - User ID changed during validation!');
            console.warn('🔒 SECURITY: Registration ID:', data.user.id);
            console.warn('🔒 SECURITY: Server profile ID:', freshUser.id);
            console.warn('🔒 SECURITY: Keeping registration ID to maintain consistency');
          }
        } catch (profileError) {
          console.warn('🔒 SECURITY: Background validation failed, but keeping registration data:', profileError);
          // Don't throw error - just log it and continue with registration data
        }
      } else {
        setCurrentUser(data.user);
      }
      
      return data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Admin registration failed');
      throw err;
    }
  };

  // Login user
  const login = async (credentials) => {
    try {
      setError('');
      const data = await authService.login(credentials);
      
      // 🔄 SIMPLIFIED APPROACH: Trust the server's login response and set user directly
      // The server has already validated the credentials and returned the correct user data
      if (data.user) {
        console.log('🔒 SECURITY: Login successful for:', data.user.email);
        setCurrentUser(data.user);
        // Initialize Pendo for logged in user
        initializePendo(data.user);
        // Initialize PostHog for logged in user
        initializePostHog(data.user, postHogContext);
        return data;
      } else {
        throw new Error('No user data received from server');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    // Track logout event before clearing user data
    postHogContext?.trackEvent('user_logout');
    // Reset PostHog user identification
    postHogContext?.resetUser();
    
    authService.logout();
    setCurrentUser(null);
  };
  
  // Request password reset
  const requestPasswordReset = async (email) => {
    try {
      setError('');
      return await authService.requestPasswordReset(email);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset request failed');
      throw err;
    }
  };
  
  // Confirm password reset
  const confirmPasswordReset = async (token, password) => {
    try {
      setError('');
      return await authService.confirmPasswordReset(token, password);
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed');
      throw err;
    }
  };

  // Update current user from cache immediately
  const updateCurrentUserFromCache = () => {
    try {
      const userData = authService.getCurrentUser();
      if (userData) {
        console.log('🔄 Updating currentUser from cache:', userData);
        setCurrentUser(userData);
        return userData;
      }
    } catch (err) {
      console.error('Error updating user from cache:', err);
    }
    return null;
  };

  // Refresh auth token
  const refreshAuthToken = async () => {
    try {
      setError('');
      const data = await authService.refreshToken();
      if (data && data.user) {
        setCurrentUser(data.user);
      }
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Token refresh failed');
      throw err;
    }
  };

  // Context value
  const value = {
    currentUser,
    loading,
    error,
    isLoggingOut, // Add logout state to context
    register,
    registerAdmin,
    login,
    logout,
    requestPasswordReset,
    confirmPasswordReset,
    refreshAuthToken,
    refreshUserProfile,
    updateCurrentUserFromCache,
    isAuthenticated: authService.isAuthenticated,
    tokenExpired,
    refreshSession,
    getCachedUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;